/**
 * The only file in game-core allowed to use the constructs the determinism
 * lint bans. Everything else goes through these helpers. Keep it tiny and
 * review every change here as if it were a rules change.
 */

const assertInt = (n: number, what: string): void => {
  if (!Number.isSafeInteger(n)) throw new RangeError(`${what}: expected a safe integer, got ${String(n)}`);
};

/** Integer division, truncating towards zero. Both arguments must be integers. */
export const idiv = (a: number, b: number): number => {
  assertInt(a, 'idiv');
  assertInt(b, 'idiv');
  if (b === 0) throw new RangeError('idiv by zero');
  return Math.trunc(a / b);
};

/** `value × pct ÷ 100` as an integer. Used for the type chart, STAB and falloff. */
export const percent = (value: number, pct: number): number => idiv(value * pct, 100);

/** A total order on strings (IDs), so ties in comparators are always broken the same way. */
export const compareIds = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

/** Object keys in a fixed order, so iteration never depends on insertion order. */
export const sortedKeys = <T extends object>(obj: T): (keyof T & string)[] =>
  (Object.keys(obj) as (keyof T & string)[]).sort(compareIds);

export const sortedEntries = <T extends object>(obj: T): [keyof T & string, T[keyof T & string]][] =>
  sortedKeys(obj).map((k) => [k, obj[k]]);

/**
 * Canonical JSON: keys sorted at every level. This is what gets hashed, so it
 * refuses anything that would hash ambiguously: non-integer numbers, undefined
 * inside arrays, and objects that are not plain data (Map, Set, Date, class
 * instances). `undefined` object members are dropped.
 */
export const canonicalJson = (value: unknown): string => {
  if (value === null) return 'null';
  switch (typeof value) {
    case 'string':
    case 'boolean':
      return JSON.stringify(value);
    case 'number':
      assertInt(value, 'canonicalJson');
      return String(value);
    case 'object':
      break;
    case 'bigint':
    case 'symbol':
    case 'undefined':
    case 'function':
      throw new TypeError(`canonicalJson: cannot serialise a ${typeof value}`);
  }
  if (Array.isArray(value)) {
    // Index loop, not map: map skips holes in sparse arrays and they would vanish from the hash.
    const parts: string[] = [];
    for (let i = 0; i < value.length; i += 1) {
      const v: unknown = value[i];
      if (v === undefined) throw new TypeError('canonicalJson: undefined or a hole inside an array');
      parts.push(canonicalJson(v));
    }
    return `[${parts.join(',')}]`;
  }
  const proto: unknown = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) throw new TypeError('canonicalJson: only plain objects are allowed');
  const obj = value as Record<string, unknown>;
  return `{${sortedKeys(obj)
    .filter((k) => obj[k] !== undefined)
    .map((k) => `${JSON.stringify(k)}:${canonicalJson(obj[k])}`)
    .join(',')}}`;
};
